import { loadChunkWithRetry } from './load-chunk-with-retry.js';

const REACT_ROUTE = 'bookmarks';
const mountedPages = new WeakMap();

/**
 * Mounts the React Bookmarks list into a server-rendered page and returns a disposer.
 *
 * The page element contains the mount root (with its skeleton), the failure panel,
 * and the JSON page data as siblings.
 */
export function mountBookmarksPage(page, load = () => import('../react/bookmarks/index')) {
    if (mountedPages.has(page)) return mountedPages.get(page);

    const root = page.querySelector('[data-bookmarks-root]');
    const failure = page.querySelector('[data-bookmarks-failure]');
    const dataScript = page.querySelector('[data-bookmarks-data]');
    if (!root || !failure || !dataScript) return () => {};

    let disposed = false;
    let failed = false;
    let disposeReact;

    const clearWatchdog = () => {
        clearTimeout(Number(page.dataset.bookmarksWatchdog));
        delete page.dataset.bookmarksWatchdog;
    };
    const releaseReact = () => {
        const release = disposeReact;
        disposeReact = undefined;
        if (release) release();
    };
    const report = (error, phase) => {
        window.Sentry?.captureException?.(error, {
            tags: { react_route: REACT_ROUTE, react_phase: phase },
        });
    };
    const fail = (phase) => (error) => {
        if (disposed || failed) return;
        failed = true;
        report(error, phase);
        root.hidden = true;
        failure.hidden = false;
        queueMicrotask(releaseReact);
    };
    const onCommit = () => {
        if (disposed || failed) return;
        root.removeAttribute('aria-busy');
    };
    const dispose = () => {
        if (disposed) return;
        disposed = true;
        releaseReact();
        window.removeEventListener('pagehide', onPageHide);
        mountedPages.delete(page);
    };
    const onPageHide = (event) => {
        if (!event.persisted) dispose();
    };

    // The watchdog covers a bundle that never runs. From here the chunk and render failure paths own the panel.
    clearWatchdog();
    root.hidden = false;
    failure.hidden = true;
    mountedPages.set(page, dispose);
    window.addEventListener('pagehide', onPageHide);

    loadChunkWithRetry(load).then((module) => {
        if (disposed || failed) return;
        if (typeof module?.mountBookmarks !== 'function') {
            fail('chunk')(new Error('Bookmarks chunk has no mountBookmarks export'));
            return;
        }
        let data;
        try {
            data = JSON.parse(dataScript.textContent);
        } catch (error) {
            fail('render')(error);
            return;
        }
        disposeReact = module.mountBookmarks(root, data, {
            onCommit,
            onError: fail('render'),
            // React recovers from these errors itself, so they never reveal the panel.
            onRecoverableError: (error) => report(error, 'recoverable'),
        });
        if (failed) queueMicrotask(releaseReact);
    }, fail('chunk'));

    return dispose;
}

function start() {
    document.querySelectorAll('[data-bookmarks-page]').forEach((page) => {
        if (page.querySelector('[data-bookmarks-root]')) mountBookmarksPage(page);
    });
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
} else {
    start();
}
